import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { StreamEmbed, Standings } from "@/components/Stream";

export default function Live() {
    const [rooms, setRooms] = useState([]);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [tick, setTick] = useState(0);

    // Fetch live rooms + poll every 8s
    useEffect(() => {
        const fetchRooms = async () => {
            try {
                const r = await api.get("/live");
                setRooms(r.data);
                if (!selected && r.data.length > 0) setSelected(r.data[0].slug);
            } catch {}
        };
        fetchRooms();
        const id = setInterval(() => setTick(t => t + 1), 8000);
        return () => clearInterval(id);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const fetchRooms = async () => {
            try {
                const r = await api.get("/live");
                setRooms(r.data);
            } catch {}
        };
        if (tick > 0) fetchRooms();
    }, [tick]);

    // When a room is selected, fetch full standings
    useEffect(() => {
        if (!selected) { setDetail(null); return; }
        const fetchDetail = () => api.get(`/tournaments/${selected}/standings`).then(r => setDetail(r.data)).catch(() => {});
        fetchDetail();
        const id = setInterval(fetchDetail, 8000);
        return () => clearInterval(id);
    }, [selected]);

    const active = rooms.find(r => r.slug === selected);

    return (
        <div className="pt-[68px]" data-testid="live-page">
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 pb-12">
                <div className="flex items-center gap-4 mb-6">
                    <div className="overline">[ Live Room · Broadcast ]</div>
                    <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-signal">
                        <span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" />
                        {rooms.length} stream{rooms.length !== 1 ? "s" : ""} on air
                    </span>
                </div>
                <h1 className="font-display font-black tracking-tighter leading-[0.88] text-5xl md:text-[9vw]">
                    On <span className="text-outline-strong">air.</span>
                </h1>
                <p className="mt-8 text-secondary max-w-xl text-base leading-relaxed">
                    Every live qualifier bracket, streamed to the main channel. Standings update every eight seconds.
                </p>
            </section>

            {rooms.length === 0 && (
                <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32">
                    <div className="border border-white/10 clip-frame-sm p-24 text-center">
                        <div className="overline text-white/40 mb-3">Off air</div>
                        <div className="font-display text-3xl mb-6">No brackets are live right now.</div>
                        <Link to="/tournaments" className="btn-ghost">Browse upcoming brackets →</Link>
                    </div>
                </section>
            )}

            {rooms.length > 0 && (
                <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32">
                    {/* Room tabs */}
                    <div className="flex flex-wrap gap-3 mb-8">
                        {rooms.map(r => (
                            <button key={r.slug} onClick={() => setSelected(r.slug)}
                                data-testid={`live-room-tab-${r.slug}`}
                                className={`px-5 py-3 clip-frame-sm border transition-colors flex items-center gap-3 ${selected === r.slug ? "border-volt bg-volt/10" : "border-white/10 hover:border-white/30 bg-[#0A0A0A]"}`}>
                                <span className={`w-1.5 h-1.5 rounded-full bg-signal ${selected === r.slug ? "live-dot" : ""}`} />
                                <div className="text-left">
                                    <div className={`font-mono text-[10px] tracking-[0.22em] uppercase ${selected === r.slug ? "text-volt" : "text-white/50"}`}>{r.game.split(":")[0]}</div>
                                    <div className={`font-display font-extrabold text-sm ${selected === r.slug ? "text-white" : "text-white/70"}`}>{r.name.split("·")[0].trim()}</div>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Main split */}
                    <div className="grid lg:grid-cols-12 gap-6">
                        {/* Stream */}
                        <motion.div
                            key={selected}
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                            className="lg:col-span-8">
                            {active && (
                                <>
                                    <StreamEmbed platform={active.stream_platform} channel={active.stream_channel || active.stream_url?.split("channel=")[1]?.split("&")[0]} url={active.stream_url} />
                                    <div className="mt-6 border border-white/10 clip-frame p-6 bg-[#0A0A0A]">
                                        <div className="flex flex-wrap items-start justify-between gap-4">
                                            <div>
                                                <div className="overline mb-2 flex items-center gap-3">
                                                    <span>{active.game}</span>
                                                    {active.test_bracket && <span className="font-mono text-[9px] tracking-[0.28em] uppercase px-2 py-0.5 border border-volt/50 text-volt">TEST BRACKET · OPEN</span>}
                                                </div>
                                                <h2 className="font-display font-black tracking-tighter text-2xl md:text-3xl leading-tight">{active.name}</h2>
                                            </div>
                                            {active.test_bracket ? (
                                                <Link to={`/bracket/${active.slug}`} className="btn-primary !py-2.5 !px-4 !text-[10px]" data-testid={`open-bracket-${active.slug}`}>
                                                    Enter test bracket →
                                                </Link>
                                            ) : (
                                                <Link to={`/tournaments/${active.slug}`} className="btn-ghost !py-2.5 !px-4 !text-[10px]" data-testid={`open-full-${active.slug}`}>
                                                    Full bracket →
                                                </Link>
                                            )}
                                        </div>
                                        <div className="mt-6 grid grid-cols-3 gap-3 pt-4 border-t border-white/10 font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">
                                            <div><div className="text-white/40">{active.test_bracket ? "Format" : "Prize"}</div><div className="mt-1 text-volt text-sm normal-case tracking-normal">{active.test_bracket ? "WSOW · 3 Matches" : `$${active.prize_pool.toLocaleString()}`}</div></div>
                                            <div><div className="text-white/40">Registered</div><div className="mt-1 text-white text-sm normal-case tracking-normal">{active.registered_teams}{active.max_teams ? ` / ${active.max_teams}` : ""} squads</div></div>
                                            <div><div className="text-white/40">{active.test_bracket ? "Entry" : "Advance"}</div><div className="mt-1 text-white text-sm normal-case tracking-normal">{active.test_bracket ? "Open · No ID" : `Top ${active.advances}`}</div></div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </motion.div>

                        {/* Live standings sidebar */}
                        <motion.div
                            key={selected + "-standings"}
                            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
                            className="lg:col-span-4">
                            <div className="border border-white/10 clip-frame bg-[#0A0A0A] p-4 md:p-5">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="overline">[ Live Standings ]</div>
                                    <div className="flex items-center gap-2 font-mono text-[9px] tracking-[0.24em] uppercase text-white/40">
                                        <span className="w-1 h-1 rounded-full bg-volt live-dot" />
                                        Refresh 8s
                                    </div>
                                </div>
                                <Standings rows={detail?.standings || []} advances={detail?.tournament?.advances} compact />
                            </div>
                        </motion.div>
                    </div>
                </section>
            )}
        </div>
    );
}
